import { useState, useCallback } from 'react';
import { UploadType } from 'further-types/files';
import { Api } from 'further-types/aws';
import { round } from 'lodash';

export interface UploadFileResult {
  url: string;
  filePath: string;
  name?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export type HttpClient = <T = unknown>(
  url: string,
  options?: { params?: Record<string, unknown> },
) => Promise<{ data: T }>;

interface SignedUrlResponse {
  statusCode: number;
  data: Api.GenerateS3SignedPostUrlResponse;
}

const sendFileToAWS = (
  file: File,
  postUrl: string,
  fields: Record<string, string>,
) => {
  return new Promise<void>((resolve, reject) => {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', postUrl);

    xhr.onreadystatechange = function () {
      if (this.readyState === 4) {
        if (this.status >= 200 && this.status < 300) {
          resolve();
        } else {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(
            xhr.responseText,
            'application/xml',
          );
          const maxSizeAllowedFromResponse =
            xmlDoc.querySelector('MaxSizeAllowed')?.textContent ?? '';
          const maxSizeInMb = Number(maxSizeAllowedFromResponse) / 1_000_000;

          const errorMessageFromResponse =
            xmlDoc.querySelector('Message')?.textContent ?? '';
          const constructedErrorMessage = `${errorMessageFromResponse}${
            maxSizeAllowedFromResponse ? ` (${round(maxSizeInMb, 2)}MB)` : ''
          }`;
          const message =
            constructedErrorMessage ||
            `S3 upload failed with status ${this.status}: ${xhr.responseText}`;
          reject(new Error(message));
        }
      }
    };

    xhr.send(formData);
  });
};

const extractErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object') {
    const maybeResponse = (
      err as { response?: { data?: { responseMsg?: string } } }
    ).response;
    if (maybeResponse?.data?.responseMsg) {
      return maybeResponse.data.responseMsg;
    }
  }

  if (err instanceof Error) {
    return err.message;
  }

  return 'Upload failed';
};

export function createFileUploader(
  httpClient: HttpClient,
  signedUrlEndpoint: string,
) {
  return function useFileUploader() {
    const [called, setCalled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<UploadFileResult | null>(null);
    const [error, setError] = useState<Error | null>(null);

    const uploadFile = useCallback(
      async (
        file: File,
        uploadType: UploadType,
        options?: { firmId?: string; customFileName?: string },
      ): Promise<
        | { data: UploadFileResult; error: undefined }
        | { error: Error; data: undefined }
      > => {
        setCalled(true);
        setLoading(true);
        setError(null);

        try {
          const { data: result } = await httpClient<SignedUrlResponse>(
            signedUrlEndpoint,
            {
              params: {
                firmId: options?.firmId,
                fileName: options?.customFileName ?? file.name,
                uploadType,
                contentType: file.type,
              },
            },
          );

          if (result.statusCode !== 200) {
            throw new Error('Error when fetching signed URL');
          }

          await sendFileToAWS(file, result.data.postUrl, result.data.fields);

          const uploadResult: UploadFileResult = {
            url: result.data.getUrl,
            filePath: result.data.key,
            name: options?.customFileName ?? file.name,
          };

          setData(uploadResult);
          return { data: uploadResult, error: undefined };
        } catch (err: unknown) {
          const errorMessage = extractErrorMessage(err);
          const errorToReturn = new Error(errorMessage);
          setError(errorToReturn);
          return { error: errorToReturn, data: undefined };
        } finally {
          setLoading(false);
        }
      },
      [],
    );

    return { uploadFile, loading, data, error, called };
  };
}
